// Cart Recommendations Web Component
class CartRecommendation extends HTMLElement {
  constructor() {
    super();
    this.flickity = null;
    this.recommendationsContainer = null;
  }

  connectedCallback() {
    // Wait for DOM to be ready
    setTimeout(() => {
      this.setupSlider();
    }, 100);
  }

  setupSlider() {
    this.recommendationsContainer = this.querySelector('#cart-recommendations');
    if (!this.recommendationsContainer) return;

    // Initialize Flickity
    this.initializeFlickity();
  }

  initializeFlickity() {
    const flickityContainer = this.querySelector('#recommendations-wrapper');
    if (!flickityContainer) return;

    try {
      // Initialize Flickity with responsive options
      this.flickity = new Flickity(flickityContainer, {
        cellAlign: 'left',
        contain: true,
        wrapAround: false,
        autoPlay: false,
        draggable: true,
        freeScroll: false,
        pageDots: true,
        prevNextButtons: false,
        groupCells: window.innerWidth < 768 ? 1.8 : 1.6
      });

      // Handle window resize for responsive behavior
      window.addEventListener('resize', () => {
        if (this.flickity) {
          const newGroupCells = window.innerWidth < 768 ? 1.8 : 1.6;
          this.flickity.options.groupCells = newGroupCells;
          this.flickity.resize();
        }
      });

      console.log('Flickity initialized successfully');
      this.loadRecommendations();
    } catch (error) {
      console.warn('Flickity failed, using CSS scroll fallback:', error);
      this.fallbackToCSSScroll();
      this.loadRecommendations();
    }
  }

  fallbackToCSSScroll() {
    // Remove Flickity classes and use CSS scroll instead
    const flickityContainer = this.querySelector('#recommendations-wrapper');
    
    if (flickityContainer) {
      // Add CSS scroll styles
      flickityContainer.classList.add('recommendations-scroll-container');
      flickityContainer.style.overflowX = 'auto';
      flickityContainer.style.overflowY = 'hidden';
      flickityContainer.style.display = 'flex';
      flickityContainer.style.gap = '20px';
      flickityContainer.style.scrollBehavior = 'smooth';
      
      // Set slide styles for horizontal scroll
      const slides = this.querySelectorAll('.flickity-slide');
      slides.forEach(slide => {
        slide.style.minWidth = '280px';
        slide.style.flexShrink = '0';
      });
      
      console.log('Fallback to CSS scroll applied');
    }
  }

  loadRecommendations() {
    // HTML is already populated by Liquid, just attach event listeners
    this.attachEventListeners();
  }

  attachEventListeners() {
    // Attach event listeners to quantity controls only
    const productActions = this.querySelectorAll('.product-actions');
    
    productActions.forEach(container => {
      const decreaseBtn = container.querySelector('.qty-decrease');
      const increaseBtn = container.querySelector('.qty-increase');
      const qtyInput = container.querySelector('.qty-input');

      // Quantity controls with form submission prevention
      decreaseBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.updateQuantity(qtyInput, -1);
      });
      
      increaseBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.updateQuantity(qtyInput, 1);
      });
      
      qtyInput?.addEventListener('input', (e) => this.handleQuantityInput(e));
      qtyInput?.addEventListener('change', () => this.validateQuantity(qtyInput));
    });

    // Add to cart button event listeners
    const addToCartBtns = this.querySelectorAll('.add-to-cart-btn');
    addToCartBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleAddToCart(e));
    });
  }

  handleQuantityInput(e) {
    // Remove any non-numeric characters
    let value = e.target.value.replace(/[^0-9]/g, '');
    
    // Ensure it's not empty
    if (value === '') {
      value = '1';
    }
    
    // Limit to 2 digits (max 99)
    if (value.length > 2) {
      value = value.slice(0, 2);
    }
    
    e.target.value = value;
  }

  updateQuantity(input, change) {
    const currentValue = parseInt(input.value) || 1;
    const newValue = Math.max(1, Math.min(99, currentValue + change));
    input.value = newValue.toString();
  }

  validateQuantity(input) {
    const value = parseInt(input.value);
    if (isNaN(value) || value < 1) {
      input.value = 1;
    } else if (value > 99) {
      input.value = 99;
    }
  }

  handleAddToCart(e) {
    e.preventDefault();
    e.stopPropagation();

    const button = e.target;
    const productId = button.dataset.productId;
    const variantId = parseInt(button.dataset.variantId);
    
    // Find the corresponding quantity input
    const qtyInput = this.querySelector(`#qty-${productId}`);
    const quantity = parseInt(qtyInput?.value) || 1;

    // Validate variant ID
    if (isNaN(variantId)) {
      console.error('Invalid variant ID:', button.dataset.variantId);
      this.showAddToCartError(button);
      return;
    }

    // Disable button during add to cart
    button.disabled = true;
    button.textContent = 'Adding...';

    // Add to cart using existing Shopify cart API
    if (window.Shopify && window.Shopify.theme && window.Shopify.theme.cart) {
      window.Shopify.theme.cart.addItem(variantId, { quantity: quantity })
        .then(() => {
          // Get updated cart and refresh view
          return window.Shopify.theme.cart.getCart();
        })
        .then(cart => {
          // Get cart config
          const cartConfig = document.getElementById('cart-config');
          const config = cartConfig ? JSON.parse(cartConfig.innerHTML || '{}') : {};
          
          // Update cart view using existing function
          if (window.Shopify && window.Shopify.theme && window.Shopify.theme.ajaxCart) {
            window.Shopify.theme.ajaxCart.updateView(config, cart);
          }
          
          // Show success message
          this.showAddToCartSuccess(button);
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          this.showAddToCartError(button);
        });
    } else {
      console.error('Shopify cart API not available');
      this.showAddToCartError(button);
    }
  }

  showAddToCartSuccess(button) {
    const originalText = button.textContent;
    button.textContent = 'Added!';
    button.style.backgroundColor = '#4CAF50';
    
    setTimeout(() => {
      button.textContent = originalText;
      button.style.backgroundColor = '';
      button.disabled = false;
    }, 2000);
  }

  showAddToCartError(button) {
    button.textContent = 'Error';
    button.style.backgroundColor = '#f44336';
    
    setTimeout(() => {
      button.textContent = 'Add to Cart';
      button.style.backgroundColor = '';
      button.disabled = false;
    }, 2000);
  }
}

// Register the custom element
customElements.define('cart-recommendation', CartRecommendation);

// Cart Properties Toggle Function
function toggleCartProperties(itemId) {
  console.log('Toggling properties for item:', itemId);
  
  const content = document.getElementById(`properties-${itemId}`);
  const chevronUp = document.getElementById(`chevron-up-${itemId}`);
  const chevronDown = document.getElementById(`chevron-down-${itemId}`);
  
  console.log('Found elements:', { content, chevronUp, chevronDown });
  
  if (content && chevronUp && chevronDown) {
    const isHidden = content.style.display === 'none' || content.style.display === '';
    
    if (isHidden) {
      // Show content
      content.style.display = 'block';
      chevronUp.style.display = 'inline';
      chevronDown.style.display = 'none';
      console.log('Showing content for item:', itemId);
    } else {
      // Hide content
      content.style.display = 'none';
      chevronUp.style.display = 'none';
      chevronDown.style.display = 'inline';
      console.log('Hiding content for item:', itemId);
    }
  } else {
    console.error('Could not find all required elements for item:', itemId);
  }
}

// Gift Message Custom Web Component
class CartMessage extends HTMLElement {
  constructor() {
    super();
    this.giftToggle = null;
    this.giftContent = null;
    this.textarea = null;
    this.charCount = null;

    this.initialize();
  }


  initialize() {
    // Get elements within this component
    this.giftToggle = this.querySelector('#gift-toggle');
    this.giftContent = this.querySelector('.gift-message-content');
    this.textarea = this.querySelector('#note');
    this.charCount = this.querySelector('.gift-character-count');
    
    // Only initialize if elements exist
    if (!this.giftToggle || !this.giftContent || !this.textarea || !this.charCount) {
      console.warn('CartMessage: Some elements not found');
      return;
    }
    this.setupEventListeners();
    this.updateCharacterCount();
  }

  setupEventListeners() {
    // Toggle functionality
    this.giftToggle.addEventListener('change', () => this.handleToggle());
    
    // Character counting functionality
    this.textarea.addEventListener('input', () => this.updateCharacterCount());
  }

  handleToggle() {
    if (this.giftToggle.checked) {
      this.giftContent.style.display = 'block';
      // this.textarea.focus();
    } else {
      this.giftContent.style.display = 'none';
    }

    this.updateCharacterCount();
  }

  updateCharacterCount() {
    const remaining = 250 - this.textarea.value.length;
    this.charCount.textContent = `${remaining} characters remaining`;
    
    if (remaining < 0) {
      this.charCount.style.color = '#ff0000';
    } else {
      this.charCount.style.color = '#999';
    }
  }

}

// Register the custom element
customElements.define('cart-message', CartMessage);
